import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
// PR-72 Commit 3 fixup (dispatch 413): cache invalidation lives at the
// writer boundary. Every successful Settings-list mutation must
// invalidate the neutral cache module so subsequent reads see the write.
// See services/settingsListCache.js for the design rationale.
import { invalidate as invalidateSettingsListCache, QUOTE_DEFAULTS_SIGNAL, COMPANY_PROFILE_SIGNAL, BRANDING_SIGNAL } from './settingsListCache';
import { getCached as getCachedSettingsList, setCached as setCachedSettingsList, drop as dropSettingsListCache } from './settingsListCache';
import { reportApiError } from '../hooks/useTelemetry';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bidit.com';

// REQ-WIRE-01a: map HTTP status / server payload to a friendly, actionable message
// instead of the raw axios "Request failed with status code NNN".
function friendlyError(status, data) {
  const serverMsg =
    (data && (data.error || data.message || (data.data && data.data.message))) || '';
  switch (status) {
    case 400:
      return serverMsg || 'We could not save that — please check the fields and try again.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'That item could not be found. It may have been removed.';
    case 405:
      return 'This action is not available right now. Please refresh and try again.';
    case 409:
      return serverMsg || 'That change conflicts with existing data.';
    case 413:
      return 'That file or request is too large.';
    case 429:
      return 'Too many requests — please wait a moment and try again.';
    default:
      if (status && status >= 500) return 'Something went wrong on our end. Please try again shortly.';
      if (!status) return 'Network error — check your connection and try again.';
      return serverMsg || 'Something went wrong. Please try again.';
  }
}

// IDI-100 — the single unwrapper for list endpoints.
//
// Context: `apiClient.get()` already strips the outer `{success, data}`
// envelope. What's left, for a LIST endpoint, is either:
//   - a raw array (legacy shape / no `limit` param passed — the backend's
//     "only wrap when the caller opted into pagination" contract, see
//     cmd/customers/main.go listCustomers and the matching pattern in
//     cmd/quotes/main.go listQuotes), or
//   - `{ <collectionKey>: [...], has_more, next_cursor? }` when a `limit`
//     was passed.
//
// The `res?.data || res || []` idiom that grew up around this ad hoc (15
// call sites, per IDI-100's sweep) silently degrades ANY shape mismatch to
// `[]` — which renders as "no results", indistinguishable from a genuinely
// empty collection. That is exactly how a 607-customer tenant ended up
// looking empty. This helper never does that: an unrecognised shape is a
// bug, and it reports as one (console.error + return null) rather than a
// blank list a developer has no way to notice.
//
// @param {*} res - already-unwrapped response body (post apiClient.get())
// @param {string} collectionKey - the enveloped shape's array key, e.g.
//   'customers', 'quotes'. Required — there is no universal default because
//   the whole point is that each endpoint's key is a specific contract.
// @param {string} [context] - optional label for the error/warning (e.g.
//   the calling component's name) to make a misuse easy to trace.
// @returns {{ list: Array, hasMore: boolean, nextCursor: string|null }}
export function unwrapList(res, collectionKey, context = 'unwrapList') {
  if (Array.isArray(res)) {
    return { list: res, hasMore: false, nextCursor: null };
  }
  if (res && typeof res === 'object' && Array.isArray(res[collectionKey])) {
    return {
      list: res[collectionKey],
      hasMore: !!res.has_more,
      nextCursor: res.next_cursor || null,
    };
  }
  // IDI-100 requirement: never silently return []. An unrecognised shape
  // is a contract break between this reader and its endpoint — surface it
  // loudly (console.error, visible in any error-tracking integration) so
  // the next endpoint that grows an envelope doesn't repeat this bug for
  // months undetected. Callers still get a safe, render-able empty list
  // back (a crashed droplist is worse than an empty one), but the failure
  // is no longer indistinguishable from "legitimately no results".
  // eslint-disable-next-line no-console
  console.error(
    `[${context}] unwrapList: unrecognised response shape for collectionKey="${collectionKey}" — ` +
    `expected an array or { ${collectionKey}: [...] }, got:`,
    res,
  );
  return { list: [], hasMore: false, nextCursor: null };
}

// IDI-121: coalesce concurrent refreshes. A screen fires several requests at
// once, and when the token lapses they all 401 together. They must share ONE
// Cognito refresh (and ONE retry each), not stampede the token endpoint.
let inflightRefresh = null;
function refreshIdTokenOnce(fetchSession = fetchAuthSession) {
  if (!inflightRefresh) {
    inflightRefresh = (async () => {
      try {
        const s = await fetchSession({ forceRefresh: true });
        return s?.tokens?.idToken?.toString() || null;
      } catch {
        return null; // refresh token revoked/expired: caller signs out
      } finally {
        // Release on the next tick so every request in the same burst reuses
        // this result, but a LATER lapse triggers a new refresh.
        setTimeout(() => { inflightRefresh = null; }, 0);
      }
    })();
  }
  return inflightRefresh;
}

// IDI-63 (D1): the address picks the dealer. Only pages at or under
// /super-admin/dealers/<id> act on a dealer (IDI-622 D4: the bare dealer page
// and every tab too); Contractors (/super-admin/dealers) and every other page
// return null.
function dealerIdFromPath(pathname) {
  const m = /^\/super-admin\/dealers\/([^/]+)(\/|$)/.exec(pathname || '');
  return m ? decodeURIComponent(m[1]) : null;
}

// IDI-833: a JWT payload is base64url (Cognito issues '-' / '_' and no
// padding); atob wants standard base64.
function decodeJwtPayload(token) {
  let s = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  return JSON.parse(atob(s));
}

// IDI-453: the row a resource / material item action names.
function resourceRef(ref) {
  const r = ref || {};
  if (r.id != null && r.id !== '') return { resource_id: String(r.id) };
  return { resource_name: r.name || '' };
}
function materialRef(ref) {
  const r = ref || {};
  if (r.id != null && r.id !== '') return { material_id: String(r.id) };
  return { material_index: r.index, ...(r.name ? { material_name: r.name } : {}) };
}

class APIClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      // STOPGAP (IDI-56, 2026-08-14): list endpoints ignore `limit` and return the
      // whole table — /v1/quotes is 2,456 rows / 2.34 MB and takes 6–20s on tenant
      // 270. Raised from 10s so the page loads at all. REVERT TO 10000 once the
      // endpoints paginate. Ceiling is ~29s (API Gateway integration timeout).
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and tenant ID
    this.client.interceptors.request.use(
      async (config) => {
        try {
          // Get token from Amplify session
          let token = null;
          try {
            const session = await fetchAuthSession({ forceRefresh: false });
            token = session.tokens?.idToken?.toString();
          } catch {
            // Not authenticated — token stays null
            token = localStorage.getItem('authToken'); // Fallback for dev
          }

          // IDI-44: when we have a live session token, the JWT tenant claim is
          // authoritative. Always re-derive it (never reuse a stale cached
          // value from a prior session) and re-sync localStorage so other
          // readers stay correct. This is what caused the sample dealer to
          // send a previous user's tenant in X-Tenant-Id and read their data.
          let tenantId;
          let dealerHeader = null;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            let groups = [];
            try {
              const payload = decodeJwtPayload(token);
              tenantId = payload['custom:tenant_id'] || null;
              const g = payload['cognito:groups'];
              groups = Array.isArray(g) ? g : [];
            } catch { tenantId = null; }
            // IDI-833 (D6): a super-admin by Cognito group on a dealer's page
            // sends that dealer, whether or not its token carries its own
            // custom:tenant_id. The server trusts it only for the group (D3).
            const isGroupSuperAdmin = groups.includes('super_admin') || groups.includes('idi_admin');
            const pathDealer = typeof window !== 'undefined'
              ? dealerIdFromPath(window.location.pathname) : null;
            if (isGroupSuperAdmin && pathDealer) dealerHeader = pathDealer;
            if (tenantId) {
              localStorage.setItem('tenantId', tenantId);
            } else {
              // Authenticated but no tenant claim on the token: do NOT fall back
              // to a cached value from another session. Clear it and let the
              // backend resolve the tenant from the verified JWT / fail closed.
              localStorage.removeItem('tenantId');
              // IDI-63 (D1): a super-admin (no tenant claim) on a dealer's
              // workspace page sends that dealer. Read from the URL, so a
              // reload keeps it; the server ignores it for anyone else (F1).
              const dealerId = typeof window !== 'undefined'
                ? dealerIdFromPath(window.location.pathname) : null;
              if (dealerId) config.headers['X-Tenant-Id'] = dealerId;
            }
          } else {
            // No session token (dev / API-key path only). Use the cached value.
            tenantId = localStorage.getItem('tenantId');
          }
          if (tenantId) {
            config.headers['X-Tenant-Id'] = tenantId;
          }
          if (dealerHeader) {
            config.headers['X-Tenant-Id'] = dealerHeader;
          }
          return config;
        } catch (error) {
          console.error('API Request interceptor error:', error);
          return config;
        }
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for handling common errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;

        // IDI-121 half B (2026-09-30): ONE refresh-and-retry before any
        // sign-out. Amplify refreshes on its own when a token is already past
        // exp (measured: fetchAuthSession issued GetTokensFromRefreshToken
        // and the request carried a fresh token). The gap was the edges: a
        // token that expires between that check and the request landing, a
        // backgrounded tab, or clock skew. Before this, the FIRST 401 signed
        // the user out on the spot. Now a 401 forces one refresh and replays
        // the request once. Only if that also fails (refresh token revoked
        // or expired, or the retry still 401s) does the sign-out below run.
        const cfg = error.config;
        const url = cfg?.url || '';
        const isAuthEndpoint = /\/auth\/(login|signup|reset)/.test(url);
        if (status === 401 && cfg && !cfg.__idi121Retried && !isAuthEndpoint) {
          cfg.__idi121Retried = true;
          const fresh = await refreshIdTokenOnce();
          if (fresh) {
            cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
            try {
              return await this.client.request(cfg);
            } catch (retryErr) {
              // Fall through with the RETRY's error, so a second 401 signs out.
              return Promise.reject(retryErr);
            }
          }
        }
        console.error('API Response error:', status, error.response?.data || error.message);
        // IDI-303: report 5xx (server-side failure) to telemetry/alerting.
        // Deliberately excludes 4xx — those are frequently expected (401 on
        // an expired session, 404 on a not-yet-created record, 400 on a
        // validation failure a user is actively fixing) and would drown a
        // 5-in-5-minutes alarm threshold in normal-flow noise. A genuine 5xx
        // is never expected — it means the backend itself broke.
        if (status >= 500) {
          reportApiError(error.config?.url || 'unknown', status, error.message);
        }
        // REQ-WIRE-01a: replace raw axios "Request failed with status code 405"
        // with a user-friendly message so settings pages can surface err.message.
        error.friendlyMessage = friendlyError(status, error.response?.data);
        error.message = error.friendlyMessage;

        // IDI-121 half A (2026-09-03): session-expired routing.
        //
        // The problem: after ~1 hour the ID token expires. The request
        // interceptor at ~:116 tries fetchAuthSession({ forceRefresh: false })
        // and if the cached idToken is stale + Amplify does not auto-refresh,
        // the request goes out without an Authorization header and the backend
        // returns 401. Currently every surface swallows that 401 into an empty
        // state or a dead toast; the user cannot recover except by refreshing
        // the tab or navigating to /login manually.
        //
        // The fix: dispatch a global event when a non-login endpoint returns
        // 401. AuthContext listens for this event and calls logout(), which
        // sets isAuthenticated=false and the route guard at App.js:~100
        // redirects to /login.
        //
        // Half B (76 bare catches, per-surface 401 rendering) is deferred
        // past launch per paste 49 §runway item 8.
        //
        // Login-URL guard: /auth/login endpoints return 401 on invalid
        // credentials. We must not signOut on those.
        //
        // 🛑 IDI-244 STOPGAP (2026-09-03, delete when IDI-244 ships):
        // `/v1/reports` returns 401 when the tenant identifier is absent
        // (cmd/reports/main.go:44). A super-admin opening Reports before
        // selecting a tenant hits that 401, which under #332's dispatch
        // would sign them out of their own admin session. Tyler's
        // IDI-244 fix changes reports 401 → 400 and sweeps the other
        // frontend-facing 401 emitters. Until that lands, /v1/reports
        // is treated the same as a login endpoint — no session-expired
        // event, no forced sign-out. Do NOT extend this suppression to
        // other endpoints; the other 401 sites are unaudited and a
        // broader guard silently re-breaks IDI-121 half A.
        //
        // DELETION TRIGGER: when IDI-244 lands and its acceptance
        // verifies (reports emits 400 not 401 on missing tenant), remove
        // the `isReportsStopgap` variable and this comment block. A
        // grep for "IDI-244 STOPGAP" finds it in one shot.
        if (status === 401 && typeof window !== 'undefined') {
          const requestUrl = error.config?.url || '';
          const isLoginAttempt = requestUrl.includes('/auth/login')
            || requestUrl.includes('/auth/signup')
            || requestUrl.includes('/auth/reset');
          // 🛑 Boundary-aware match — NOT `.includes('/v1/reports')`.
          // A stopgap scoped to four endpoints must not be silently widened
          // by future paths starting with `/v1/reports-*`. The regex matches
          // bare `/v1/reports` and `/v1/reports/*` exactly; it does not
          // match `/v1/reports-config` or similar (Portica paste 72).
          const isReportsStopgap = /^\/v1\/reports(\/|$)/.test(requestUrl);
          if (!isLoginAttempt && !isReportsStopgap) {
            try {
              window.dispatchEvent(new CustomEvent('auth:session-expired', {
                detail: { url: requestUrl, status: 401 },
              }));
            } catch (dispatchErr) {
              // window.dispatchEvent should not throw in a normal browser,
              // but guard against test-environment edge cases.
              console.error('Failed to dispatch auth:session-expired:', dispatchErr);
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async get(url, params) {
    const response = await this.client.get(url, { params });
    return response.data?.data !== undefined ? response.data.data : response.data;
  }

  async post(url, data) {
    const response = await this.client.post(url, data);
    return response.data?.data !== undefined ? response.data.data : response.data;
  }

  async put(url, data) {
    const response = await this.client.put(url, data);
    return response.data?.data !== undefined ? response.data.data : response.data;
  }

  async delete(url) {
    const response = await this.client.delete(url);
    return response.data?.data !== undefined ? response.data.data : response.data;
  }

  // ==========================================
  // High-level API methods
  // ==========================================

  // --- Customers ---
  getCustomers(params) {
    // IDI-120/IDI-141 pattern: default cap so a caller that forgets a limit
    // doesn't hit the unbounded backend path (PORTICA-56 intentionally keeps
    // that path a bare array for legacy callers -- confirmed live: currently
    // survives at the biggest tenant's 2,083 customers, but has no ceiling
    // and no has_more/total_count, unlike getJobs' already-capped default).
    // Mirrors getJobs' exact guard below (nullish check, not a spread --
    // axios strips `undefined`, which would silently un-cap a caller passing
    // `{limit: undefined}` through a spread).
    const p = params || {};
    const limit = p.limit != null ? p.limit : 250;
    return this.get('/v1/customers', { ...p, limit });
  }
  getCustomer(id) { return this.get(`/v1/customers/${id}`); }
  createCustomer(data) { return this.post('/v1/customers', data); }
  updateCustomer(id, data) { return this.put(`/v1/customers/${id}`, data); }
  deleteCustomer(id) { return this.delete(`/v1/customers/${id}`); }

  // --- Quotes ---
  getQuotes(params) { return this.get('/v1/quotes', params); }
  // IDI-56: cheap aggregate chip counts across the whole tenant, computed
  // server-side without materializing every quote. See cmd/quotes/main.go
  // getQuoteCounts — the field/predicate contract is documented there.
  getQuoteCounts() { return this.get('/v1/quotes', { action: 'counts' }); }
  getQuote(id) { return this.get(`/v1/quotes/${id}`); }
  getQuoteVersions(id) { return this.get(`/v1/quotes?action=versions&quote_id=${id}`); }
  // IDI-539/IDI-540: the quote's acknowledgment history (oldest first).
  // Resolves to { quote_id, acknowledgments: [...] }; a failed request
  // REJECTS (never a silent []), so the panel can tell error from empty.
  getQuoteAcknowledgements(quoteId) { return this.get('/v1/quotes', { action: 'acknowledgements', quote_id: quoteId }); }
  // IDI-542: the same reader, for a change order or a job.
  getChangeOrderAcknowledgements(changeOrderId) { return this.get('/v1/quotes', { action: 'acknowledgements', change_order_id: changeOrderId }); }
  getJobAcknowledgements(jobId) { return this.get('/v1/quotes', { action: 'acknowledgements', job_id: jobId }); }
  // PT-43.4 (2026-07-28): fetch the immutable AcceptedConfiguration snapshot
  // so the CO builder can seed from frozen figures, not mutable job.line_items.
  getAcceptedConfiguration(quoteId) { return this.get(`/v1/quotes/${quoteId}?action=accepted_configuration`); }
  // IDI-116: presigned GET for the customer's stored signature image.
  // Dedicated endpoint -- getDownloadUrl's tenant-prefix check can never
  // match a "signatures/<tenantId>/..." key (see cmd/quotes.
  // getSignatureDownloadURL for the full explanation).
  getSignatureDownloadUrl(quoteId) { return this.post('/v1/quotes?action=get_signature_download_url', { quote_id: quoteId }); }
  createQuote(data) { return this.post('/v1/quotes', data); }
  updateQuote(id, data) { return this.put(`/v1/quotes/${id}`, data); }
  deleteQuote(id) { return this.delete(`/v1/quotes/${id}`); }
  // sendQuote/`?action=send` removed (IDI-118, 2026-09-03): dead code —
  // requireESign has been hardcoded true since W3.6 (2026-08-04), so no UI
  // path ever called this. sendForSignature is the only send path now; the
  // backend `?action=send` alias was removed alongside this (quotes/main.go).
  activateQuote(id) { return this.post(`/v1/quotes?action=activate`, { quote_id: id }); }
  // IDI-305: the only write path for a quote's internal notes. The server
  // stamps author + time from the login and returns { id, internal_notes: [...] }.
  addQuoteNote(id, content) { return this.post(`/v1/quotes?action=add_note`, { quote_id: id, content }); }
  // IDI-552: the note's author (only) edits or deletes it; server 403s others.
  editQuoteNote(id, noteId, content) { return this.post(`/v1/quotes?action=edit_note`, { quote_id: id, note_id: noteId, content }); }
  deleteQuoteNote(id, noteId) { return this.post(`/v1/quotes?action=delete_note`, { quote_id: id, note_id: noteId }); }
  markQuoteAsLost(id, reason) { return this.post(`/v1/quotes?action=mark_as_lost`, { quote_id: id, reason }); }
  markQuoteAsExpired(id) { return this.post(`/v1/quotes?action=mark_as_expired`, { quote_id: id }); }
  archiveQuote(id) { return this.post(`/v1/quotes?action=archive`, { quote_id: id }); }
  unarchiveQuote(id) { return this.post(`/v1/quotes?action=unarchive`, { quote_id: id }); }
  requestQuoteChanges(id, changeSummary) {
    return this.post(`/v1/quotes?action=request_changes`, { quote_id: id, change_summary: changeSummary });
  }
  convertQuoteToJob(id, selections) { return this.post(`/v1/quotes?action=convert_to_job`, { quote_id: id, ...(selections && { selections }) }); }
  // IDI-416: `selections` ({alternates, options}) are recorded server-side as
  // the AcceptedConfiguration snapshot, the one record of what was accepted.
  recordDecision(quoteId, { channel, recorder, selections } = {}) { return this.post('/v1/quotes?action=record_decision', { quote_id: quoteId, channel: channel || 'phone', recorder: recorder || '', ...(selections && { selections }) }); }
  // IDI-359: optional Review & Send display toggles (e.g. { notes: false }) so
  // the PDF matches the preview; omitted → the quote's stored toggles apply.
  // IDI-487 D5: includedAttachments (the Review & Send refs to append) make
  // the answer { url, filename, content_type } — the combined file is stored
  // and linked; none → { pdf_base64, content_type, filename } as before.
  generateQuotePDF(id, displayOptions = null, includedAttachments = null) {
    const body = { quote_id: id };
    if (displayOptions) body.display_options = displayOptions;
    if (Array.isArray(includedAttachments) && includedAttachments.length) {
      body.included_attachments = includedAttachments;
    }
    return this.post(`/v1/quotes?action=generate_pdf`, body);
  }

  // IDI-541: the signed PDF for one acceptance (its AcceptedConfiguration).
  // Same response shape as generate_pdf: { pdf_base64, content_type, filename }.
  downloadSignedQuotePDF(quoteId, acceptedConfigurationId) {
    return this.post(`/v1/quotes?action=signed_pdf`, {
      quote_id: quoteId,
      accepted_configuration_id: acceptedConfigurationId,
    });
  }
  // IDI-770: the signed PDF for one change-order acceptance's stored record.
  // Same response shape as signed_pdf.
  downloadSignedChangeOrderPDF(changeOrderId, acceptedConfigurationId) {
    return this.post(`/v1/quotes?action=signed_change_order_pdf`, {
      change_order_id: changeOrderId,
      accepted_configuration_id: acceptedConfigurationId,
    });
  }
  // IDI-105: Material List. rows come from quote-builder/materialList.js;
  // omit `send` to get the PDF, pass { to: [...], message } to email it.
  getMaterialListRecipients(id) { return this.get('/v1/quotes', { action: 'material_list_recipients', quote_id: id }); }
  materialList(id, rows, send = null) { return this.post('/v1/quotes?action=material_list', { quote_id: id, rows, ...(send && { send }) }); }
  getRecentSearches() { return this.get('/v1/quotes?action=recent_searches'); }
  saveRecentSearch(term) { return this.post('/v1/quotes?action=save_search', { term }); }
  sendForSignature(id, composer = null, displayOptions = null) {
    // PT-53A W2.1 (DEF-108): pass composer along so recipients + subject +
    // message are recorded even when SES is not yet wired.
    const body = { quote_id: id };
    if (composer) body.send_composer = composer;
    if (displayOptions) body.display_options = displayOptions;
    return this.post('/v1/quotes?action=send_for_signature', body);
  }
  getSignaturePage(token) { return this.get(`/v1/signature?token=${token}`); }
  acknowledgeQuote(token, extra = {}) { return this.post('/v1/signature?action=acknowledge', { token, ...extra }); }
  declineQuote(token, reason) { return this.post('/v1/signature?action=decline', { token, reason }); }
  getUploadUrl(quoteId, filename, contentType) { return this.post('/v1/quotes?action=get_upload_url', { quote_id: quoteId, filename, content_type: contentType }); }
  getDownloadUrl(quoteId, key) { return this.post('/v1/quotes?action=get_download_url', { quote_id: quoteId, key }); }

  // PT-53b (2026-08-02): cross-scope attachment presign methods.
  // Backend accepts `owner_type` ∈ {quote, job, work_order, invoice} + `owner_id`
  // per FILES_ATTACHMENTS_model §5 (shared attach modal, four scopes). The old
  // getUploadUrl/getDownloadUrl above are kept as backward-compat wrappers; new
  // consumers should call getAttachmentUploadUrl / getAttachmentDownloadUrl.
  getAttachmentUploadUrl({ ownerType, ownerId, filename, contentType }) {
    return this.post('/v1/quotes?action=get_upload_url', {
      owner_type: ownerType,
      owner_id: ownerId,
      filename,
      content_type: contentType,
    });
  }
  getAttachmentDownloadUrl({ key }) {
    return this.post('/v1/quotes?action=get_download_url', { key });
  }
  // IDI-445 D3/D4: a new-style ref carries only its attachment id. The server
  // checks tenant, key and deletion; `download: true` saves under the filename.
  getAttachmentDownloadUrlById({ attachmentId, download = false }) {
    return this.post('/v1/quotes?action=get_download_url', { attachment_id: attachmentId, download: !!download });
  }

  // B2 attachment lifecycle: create record after S3 PUT succeeds, then confirm.
  createAttachment({ ownerType, ownerId, parentOwnerId, filename, storageKey, contentType, sizeBytes, description, sourceType, originType, originId }) {
    // PT-53b commit 2 (2026-08-03): `parentOwnerId` is required when
    // ownerType='work_order' — the backend transactional handler fetches the
    // parent job by that id to append the ref to job.work_orders[*].attachments[].
    // Other owner types ignore parentOwnerId; safe to always pass it (or omit).
    return this.post('/v1/quotes?action=create_attachment', {
      owner_type: ownerType,
      owner_id: ownerId,
      parent_owner_id: parentOwnerId || undefined,
      filename,
      storage_key: storageKey,
      content_type: contentType,
      size_bytes: sizeBytes,
      description: description || undefined,
      source_type: sourceType || undefined,
      // IDI-484: an invoice's files are stored on its JOB; origin says which
      // invoice they came from (origin_id is absent while it is being created).
      origin_type: originType || undefined,
      origin_id: originId || undefined,
    });
  }
  confirmAttachment({ attachmentId }) {
    return this.post('/v1/quotes?action=confirm_upload', { attachment_id: attachmentId });
  }
  retryAttachment({ attachmentId }) {
    return this.post('/v1/quotes?action=retry_attachment', { attachment_id: attachmentId });
  }
  // parentOwnerId: the parent job id for a work-order file, so the server
  // also removes the WO's companion reference (IDI-453).
  deleteAttachment({ attachmentId, parentOwnerId }) {
    return this.post('/v1/quotes?action=delete_attachment', {
      attachment_id: attachmentId,
      ...(parentOwnerId ? { parent_owner_id: parentOwnerId } : {}),
    });
  }

  // --- Jobs ---
  // IDI-141: default cap. Without a limit the backend returns a bare array of
  // all ~1,179 jobs (>10s -> Lambda 500). With a limit, response is wrapped
  // {jobs, has_more, next_cursor?}. Callers that need a wider window pass
  // their own limit (e.g. DashboardNew uses 500); callers that need
  // everything must page explicitly (see JobsPage.js).
  //
  // The merge uses a nullish check, NOT a spread, because axios strips
  // params whose value is `undefined` — a caller passing `{limit: undefined}`
  // through a spread would silently un-cap and re-trigger the exact 500 we're
  // fixing.
  getJobs(params) {
    const p = params || {};
    const limit = p.limit != null ? p.limit : 250;
    return this.get('/v1/jobs', { ...p, limit });
  }
  getJob(id) { return this.get(`/v1/jobs/${id}`); }
  createJob(data) { return this.post('/v1/jobs', data); }
  updateJob(id, data) { return this.put(`/v1/jobs/${id}`, data); }
  deleteJob(id) { return this.delete(`/v1/jobs/${id}`); }
  // IDI-246: complete / reopen a job (paid roles; the server closes or restores
  // its work orders and writes the Notes entry). Each resolves to the saved job.
  completeJob(id) { return this.put(`/v1/jobs/${id}`, { action: 'complete_job' }); }
  reopenJob(id) { return this.put(`/v1/jobs/${id}`, { action: 'reopen_job' }); }

  // IDI-453: change ONE embedded work order. The server merges it into the
  // fresh job record (conditional on the job's rev, retried), so two people
  // editing different WOs on the same job no longer erase each other. Never
  // PUT a whole `work_orders` list rebuilt in the browser: the server refuses
  // it without a matching `rev` (409).
  updateWorkOrder(jobId, workOrderId, changes) {
    return this.put(`/v1/jobs/${jobId}`, { action: 'update_work_order', work_order_id: workOrderId, changes });
  }
  createWorkOrder(jobId, workOrder) {
    return this.put(`/v1/jobs/${jobId}`, { action: 'create_work_order', work_order: workOrder });
  }
  // IDI-468/469: delete one embedded work order (any state; paid roles only).
  deleteWorkOrder(jobId, workOrderId) {
    return this.put(`/v1/jobs/${jobId}`, { action: 'delete_work_order', work_order_id: workOrderId });
  }
  // IDI-453 (same work order): item-level writes. Each changes ONE item on the
  // fresh job record under the rev guard, so two people on one work order
  // never overwrite each other's notes, time, check-outs or Qty Used. The
  // server stamps ids, authors and actors; the page never sends whole arrays.
  // Each resolves to the saved job.
  woItemAction(jobId, action, workOrderId, fields) {
    return this.put(`/v1/jobs/${jobId}`, { action, work_order_id: workOrderId, ...(fields || {}) });
  }
  addWorkOrderNote(jobId, workOrderId, content) {
    return this.woItemAction(jobId, 'add_wo_note', workOrderId, { content });
  }
  addJobNote(jobId, content) {
    return this.put(`/v1/jobs/${jobId}`, { action: 'add_job_note', content });
  }
  // IDI-553: the author edits/deletes their own job note (no workOrderId) or
  // work-order note. Author-only for every role; enforced by the API.
  editNote(jobId, noteId, content, workOrderId) {
    return this.put(`/v1/jobs/${jobId}`, {
      action: 'edit_note', note_id: noteId, content,
      ...(workOrderId ? { work_order_id: workOrderId } : {}),
    });
  }
  deleteNote(jobId, noteId, workOrderId) {
    return this.put(`/v1/jobs/${jobId}`, {
      action: 'delete_note', note_id: noteId,
      ...(workOrderId ? { work_order_id: workOrderId } : {}),
    });
  }
  // memberId/memberName: clocking in for another member (B-14).
  addTimeEntry(jobId, workOrderId, { memberId, memberName, clockIn } = {}) {
    return this.woItemAction(jobId, 'add_time_entry', workOrderId, {
      ...(memberId ? { member_id: memberId } : {}),
      ...(memberName ? { member_name: memberName } : {}),
      ...(clockIn ? { clock_in: clockIn } : {}),
    });
  }
  closeTimeEntry(jobId, workOrderId, entryId) {
    return this.woItemAction(jobId, 'close_time_entry', workOrderId, entryId ? { entry_id: entryId } : {});
  }
  editTimeEntry(jobId, workOrderId, entryId, entry) {
    return this.woItemAction(jobId, 'edit_time_entry', workOrderId, { entry_id: entryId, entry });
  }
  // ref: { id } or, for a legacy id-less / string row, { name }.
  checkOutResource(jobId, workOrderId, ref) {
    return this.woItemAction(jobId, 'check_out_resource', workOrderId, resourceRef(ref));
  }
  checkInResource(jobId, workOrderId, ref) {
    return this.woItemAction(jobId, 'check_in_resource', workOrderId, resourceRef(ref));
  }
  // ref: { id } or, for a legacy id-less row, { index, name }.
  adjustMaterialQtyUsed(jobId, workOrderId, ref, delta) {
    return this.woItemAction(jobId, 'adjust_material_qty_used', workOrderId, { ...materialRef(ref), delta });
  }
  setMaterialHidden(jobId, workOrderId, ref, hidden) {
    return this.woItemAction(jobId, 'set_material_hidden', workOrderId, { ...materialRef(ref), hidden: !!hidden });
  }
  // NOTE: Work orders are NOT a standalone resource. They are embedded in the
  // parent job record as job.work_orders[] and are read/written exclusively
  // through the /v1/jobs endpoints (see WorkOrderDetail.js / JobDetail.js).
  // The previous /v1/work-orders methods targeted a table that was never
  // created (bidit-dev-workorders) and always returned HTTP 500, so they have
  // been removed (M-3 / Iteration 12 audit). Do NOT re-add a standalone WO API
  // without first provisioning the table + GSIs in terraform.

  // --- Calendar ---
  getCalendarEvents(params) { return this.get('/v1/calendar/events', params); }
  createCalendarEvent(data) { return this.post('/v1/calendar/events', data); }
  updateCalendarEvent(id, data) { return this.put(`/v1/calendar/events/${id}`, data); }
  deleteCalendarEvent(id) { return this.delete(`/v1/calendar/events/${id}`); }

  // --- Change Orders ---
  getChangeOrders(jobId) { return this.get(`/v1/jobs/${jobId}/change-orders`); }
  getChangeOrder(id) { return this.get(`/v1/change-orders/${id}`); }
  // IDI-460 (D1): the customer projection (IDI-302): lines, net, tax, total.
  getChangeOrderCustomerView(id) { return this.get(`/v1/change-orders/${id}/customer-view`); }
  // IDI-298: the server PDF of the customer view. Body {displayOptions, notes}
  // for a Draft/Rejected (as previewed); {} for Sent/Approved (stored only).
  generateChangeOrderPDF(id, body = {}) { return this.post(`/v1/change-orders/${id}/pdf`, body || {}); }
  createChangeOrder(data) { return this.post('/v1/change-orders', data); }
  updateChangeOrder(id, data) { return this.put(`/v1/change-orders/${id}`, data); }
  sendChangeOrder(id, sendData) { return this.post(`/v1/change-orders/${id}/send`, sendData); }
  applyChangeOrder(id) { return this.post(`/v1/change-orders/${id}/apply`, {}); }
  // IDI-639: Reopen and Edit (sent, declined, accepted → draft; link stops).
  reopenChangeOrder(id) { return this.post(`/v1/change-orders/${id}/reopen`, {}); }
  // IDI-460: forward `notes`. The dialog collects it and the backend stores it
  // as decisionNotes, but this call used to drop it, so no note was ever saved.
  // IDI-547: a hand-recorded acceptance is "manual" — no channel picker.
  recordChangeOrderDecision(id, { channel, recorder, notes } = {}) { return this.post(`/v1/change-orders/${id}/record-decision`, { channel: channel || 'manual', recorder: recorder || '', notes: notes || '' }); }

  // --- Invoices ---
  getInvoices(params) { return this.get('/v1/invoices', params); }
  getInvoice(id) { return this.get(`/v1/invoices/${id}`); }
  createInvoice(data) { return this.post('/v1/invoices', data); }
  // IDI-450: price an invoice server-side without saving it, so the Review &
  // Send preview and the created invoice can never disagree (same handler,
  // same records, including applied change orders).
  previewInvoice(data) { return this.post('/v1/invoices?action=preview', data); }
  // IDI-451: deposit + draft completion in one request (one transaction).
  createSplit(data) { return this.post('/v1/invoices?action=create_split', data); }
  updateInvoice(id, data) { return this.put(`/v1/invoices/${id}`, data); }
  deleteInvoice(id) { return this.delete(`/v1/invoices/${id}`); }
  // IDI-519: `extra` carries an existing draft's terms (editable until sent).
  sendInvoice(id, extra = {}) { return this.post(`/v1/invoices?action=send`, { ...extra, invoice_id: id }); }
  // IDI-518: `paidDate` is the "Paid on" calendar day (YYYY-MM-DD).
  markInvoicePaid(id, paidDate) {
    const body = { invoice_id: id };
    if (paidDate) body.paid_date = paidDate;
    return this.post(`/v1/invoices?action=mark_paid`, body);
  }
  // IDI-518: paid -> sent; clears the paid date.
  markInvoiceUnpaid(id) { return this.post(`/v1/invoices?action=mark_unpaid`, { invoice_id: id }); }
  exportInvoiceQBO(id) { return this.post(`/v1/invoices?action=export_qbo`, { invoice_id: id }); }
  validateInvoiceExport(id) { return this.post(`/v1/invoices?action=validate_export`, { invoice_id: id }); }
  generateInvoicePDF(id) { return this.post(`/v1/invoices?action=generate_pdf`, { invoice_id: id }); }
  // IDI-511: render a Review & Send PREVIEW from the page's values; creates
  // no invoice and uses no number.
  previewInvoicePDF(data) { return this.post('/v1/invoices?action=generate_pdf', data); }

  // --- Settings ---
  getSettings() { return this.get('/v1/settings'); }
  getUsers() { return this.get('/v1/settings?action=list_users'); }
  // IDI-718: a whole-record save carrying job / work order types (Jobs & Work
  // Orders) re-reads those lists after success, so pickers and labels follow.
  async updateSettings(data) {
    const res = await this.put('/v1/settings', data);
    const has = (k) => !!data && Object.prototype.hasOwnProperty.call(data, k);
    if (has('jobTypes')) invalidateSettingsListCache('job_types');
    if (has('workOrderTypes')) invalidateSettingsListCache('work_order_types');
    return res;
  }
  getTemplates(propertyType) {
    const params = propertyType ? `?property_type=${propertyType}` : '';
    return this.get(`/v1/settings/templates${params}`);
  }
  updateTemplates(data) { return this.put('/v1/settings/templates', data); }

  // --- Onboarding ---
  getOnboardingState() { return this.get('/v1/onboarding'); }
  updateOnboardingStep(step, data, skipped) { return this.post('/v1/onboarding', { step, data, skipped }); }
  claimAccount(code) { return this.post('/v1/onboarding?action=claim', { claim_code: code }); }
  inviteFieldTech(email, name) { return this.post('/v1/onboarding?action=invite_tech', { email, name }); }
  importCustomers(customers, dryRun) { return this.post('/v1/onboarding?action=import_customers', { customers, dry_run: dryRun }); }
  completeOnboarding() { return this.post('/v1/onboarding?action=complete', {}); }

  // --- Tax ---
  getTaxStates() { return this.get('/v1/tax?action=all_states'); }
  getTaxState(state) { return this.get(`/v1/tax?action=lookup_state&state=${state}`); }
  calculateTax(data) { return this.post('/v1/tax?action=calculate', data); }

  // --- Telemetry ---
  emitEvent(eventName, properties, idempotencyKey) {
    return this.post('/v1/telemetry', { event_name: eventName, properties, idempotency_key: idempotencyKey });
  }

  // --- Reports ---
  getReportSummary(params) { return this.get('/v1/reports/summary', params); }
  getReportRevenue(params) { return this.get('/v1/reports/revenue', params); }
  getReportQuotes(params) { return this.get('/v1/reports/quotes', params); }
  getReportJobs(params) { return this.get('/v1/reports/jobs', params); }
  getReportMargins(params) { return this.get('/v1/reports/margins', params); }
}

const apiClient = new APIClient();

// Named exports for backward compatibility
export const customerAPI = {
  getCustomers: (params) => apiClient.getCustomers(params),
  getCustomer: (id) => apiClient.getCustomer(id),
  createCustomer: (data) => apiClient.createCustomer(data),
  updateCustomer: (id, data) => apiClient.updateCustomer(id, data),
  deleteCustomer: (id) => apiClient.deleteCustomer(id),
};

export const quoteAPI = {
  getQuotes: (params) => apiClient.getQuotes(params),
  getQuoteCounts: () => apiClient.getQuoteCounts(),
  getQuote: (id) => apiClient.getQuote(id),
  getQuoteAcknowledgements: (quoteId) => apiClient.getQuoteAcknowledgements(quoteId),
  getChangeOrderAcknowledgements: (coId) => apiClient.getChangeOrderAcknowledgements(coId),
  getJobAcknowledgements: (jobId) => apiClient.getJobAcknowledgements(jobId),
  downloadSignedQuotePDF: (quoteId, configId) => apiClient.downloadSignedQuotePDF(quoteId, configId),
  downloadSignedChangeOrderPDF: (coId, configId) => apiClient.downloadSignedChangeOrderPDF(coId, configId),
  getAcceptedConfiguration: (quoteId) => apiClient.getAcceptedConfiguration(quoteId),
  getSignatureDownloadUrl: (quoteId) => apiClient.getSignatureDownloadUrl(quoteId),
  createQuote: (data) => apiClient.createQuote(data),
  updateQuote: (id, data) => apiClient.updateQuote(id, data),
  deleteQuote: (id) => apiClient.deleteQuote(id),
  // sendQuote removed (IDI-118, 2026-09-03): dead code, see apiClient above.
  activateQuote: (id) => apiClient.activateQuote(id),
  addQuoteNote: (id, content) => apiClient.addQuoteNote(id, content),
  editQuoteNote: (id, noteId, content) => apiClient.editQuoteNote(id, noteId, content),
  deleteQuoteNote: (id, noteId) => apiClient.deleteQuoteNote(id, noteId),
  markAsLost: (id, reason) => apiClient.markQuoteAsLost(id, reason),
  markAsExpired: (id) => apiClient.markQuoteAsExpired(id),
  archiveQuote: (id) => apiClient.archiveQuote(id),
  unarchiveQuote: (id) => apiClient.unarchiveQuote(id),
  // convertToJob wrapper removed (DEF-141b, 2026-07-31): bypassed the
  // ConvertToJobModal selection UI — use api.convertQuoteToJob(id, selections)
  // via the modal instead. See quote-builder/quoteAPI.js for the full note.
  generatePDF: (id, displayOptions, includedAttachments) => apiClient.generateQuotePDF(id, displayOptions, includedAttachments),
};

export const jobAPI = {
  getJobs: (params) => apiClient.getJobs(params),
  getJob: (id) => apiClient.getJob(id),
  createJob: (data) => apiClient.createJob(data),
  updateJob: (id, data) => apiClient.updateJob(id, data),
  updateWorkOrder: (jobId, woId, changes) => apiClient.updateWorkOrder(jobId, woId, changes),
  createWorkOrder: (jobId, wo) => apiClient.createWorkOrder(jobId, wo),
  deleteWorkOrder: (jobId, woId) => apiClient.deleteWorkOrder(jobId, woId),
  addWorkOrderNote: (jobId, woId, content) => apiClient.addWorkOrderNote(jobId, woId, content),
  addJobNote: (jobId, content) => apiClient.addJobNote(jobId, content),
  editNote: (jobId, noteId, content, woId) => apiClient.editNote(jobId, noteId, content, woId),
  deleteNote: (jobId, noteId, woId) => apiClient.deleteNote(jobId, noteId, woId),
  deleteJob: (id) => apiClient.deleteJob(id),
  completeJob: (id) => apiClient.completeJob(id),
  reopenJob: (id) => apiClient.reopenJob(id),
  updateStatus: (id, status) => apiClient.put(`/v1/jobs/${id}`, { status }),
};

export const invoiceAPI = {
  getInvoices: (params) => apiClient.getInvoices(params),
  getInvoice: (id) => apiClient.getInvoice(id),
  createInvoice: (data) => apiClient.createInvoice(data),
  previewInvoice: (data) => apiClient.previewInvoice(data),
  createSplit: (data) => apiClient.createSplit(data),
  updateInvoice: (id, data) => apiClient.updateInvoice(id, data),
  deleteInvoice: (id) => apiClient.deleteInvoice(id),
  sendInvoice: (id, extra) => apiClient.sendInvoice(id, extra),
  markPaid: (id, paidDate) => apiClient.markInvoicePaid(id, paidDate),
  markUnpaid: (id) => apiClient.markInvoiceUnpaid(id),
  exportQBO: (id) => apiClient.exportInvoiceQBO(id),
  validateExport: (id) => apiClient.validateInvoiceExport(id),
  generatePDF: (id) => apiClient.generateInvoicePDF(id),
  previewPDF: (data) => apiClient.previewInvoicePDF(data),
};

export const settingsAPI = {
  getSettings: () => apiClient.getSettings(),
  // IDI-714: setup's finish carries quoteDefaults only when a term was picked
  // (buildSettingsPayload strips undefined keys); signal only then, and only
  // after a successful write.
  updateSettings: async (data) => {
    const res = await apiClient.updateSettings(data);
    if (data && Object.prototype.hasOwnProperty.call(data, 'quoteDefaults')) {
      invalidateSettingsListCache(QUOTE_DEFAULTS_SIGNAL);
    }
    return res;
  },
  getTemplates: (propertyType) => apiClient.getTemplates(propertyType),
  updateTemplates: (data) => apiClient.updateTemplates(data),

  // Company Profile
  getCompanyProfile: () => apiClient.get('/v1/settings?action=company_profile'),
  // IDI-718: a successful save re-reads the app's company-profile copy.
  updateCompanyProfile: async (data) => {
    const res = await apiClient.put('/v1/settings', { action: 'company_profile', ...data });
    invalidateSettingsListCache(COMPANY_PROFILE_SIGNAL);
    return res;
  },

  // Users & Roles
  getUsers: () => apiClient.get('/v1/settings?action=list_users').then(r => Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.users) ? r.users : []),
  // IDI-134: the send composer's CC type-ahead. scope=tenant limits the list to
  // the active tenant for every caller, super-admins included (getUsers keeps
  // super-admin cross-tenant listing for Users & Roles).
  getTenantUsers: () => apiClient.get('/v1/settings?action=list_users&scope=tenant').then(r => Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.users) ? r.users : []),
  // IDI-622 (D3): BIDIT's own Last Login record, on every sign-in / app open.
  // Not a users-API call (IDI-625's gate), never a Cognito write.
  recordLogin: () => apiClient.post('/v1/settings', { action: 'record_login' }),
  // IDI-202: the migrated legacy user roster (read-only; does NOT invite
  // anyone). Used to show "Not Invited" rows the live Cognito-backed getUsers
  // list has no way to surface (a migrated user has no Cognito account at all
  // until someone explicitly invites them via addUser).
  // IDI-293: same idiom as getList; the backend (listUserRoster) fails closed
  // on purpose and this reader was converting that back into "No users found".
  getUserRoster: () => apiClient.get('/v1/settings?action=list&list_type=user_roster').then(r => unwrapList(r, Array.isArray(r?.data) || !Array.isArray(r?.items) ? 'data' : 'items', 'settingsAPI.getUserRoster').list),
  // IDI-52 (B3): migrated field-technician roster for the WO assignment picker.
  getFieldTechnicians: () => apiClient.get('/v1/settings?action=list&list_type=field_technicians').then(r => Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.items) ? r.items : []),
  addUser: (data) => apiClient.post('/v1/settings?action=add_user', data),
  deactivateUser: (userId) => apiClient.post('/v1/settings?action=deactivate_user', { user_id: userId }),
  updateUserSeatType: (userId, seatType) => apiClient.put('/v1/settings', { action: 'update_seat_type', user_id: userId, seat_type: seatType }),
  // IDI-226: the signed-in dealer's own paid-seat number (null = none, D4).
  getPaidSeats: () => apiClient.get('/v1/settings?action=list_users&view=seats')
    .then((r) => { const n = Number(r?.paidSeats ?? r?.data?.paidSeats); return n > 0 ? n : null; }),
  // IDI-79: recovery path when the one-time temp password was lost/never
  // delivered. Backend regenerates a fresh temporary password (returned here
  // for the same on-screen fallback UX as addUser) and re-sends the BIDIT-
  // branded invite email.
  resendInvite: (userId) => apiClient.post('/v1/settings?action=resend_invite', { user_id: userId }),

  // Margins & Labor
  getMargins: () => apiClient.get('/v1/settings?action=margins'),
  updateMargins: (data) => apiClient.put('/v1/settings', { action: 'margins', ...data }),

  // Tax & Rates
  getTaxRates: () => apiClient.get('/v1/settings?action=tax_rates'),
  updateTaxRates: (data) => apiClient.put('/v1/settings', { action: 'tax_rates', ...data }),

  // Branding
  getBranding: () => apiClient.get('/v1/settings?action=branding'),
  // IDI-718: a successful save re-reads the app's branding copy.
  updateBranding: async (data) => {
    const res = await apiClient.put('/v1/settings', { action: 'branding', ...data });
    invalidateSettingsListCache(BRANDING_SIGNAL);
    return res;
  },

  // Quote Defaults
  getQuoteDefaults: () => apiClient.get('/v1/settings?action=quote_defaults'),
  // IDI-714: a successful save tells SettingsProvider to re-read its copy.
  updateQuoteDefaults: async (data) => {
    const res = await apiClient.put('/v1/settings', { action: 'quote_defaults', ...data });
    invalidateSettingsListCache(QUOTE_DEFAULTS_SIGNAL);
    return res;
  },

  // Templates
  getQuoteTemplates: () => apiClient.get('/v1/settings?action=quote_templates').then(r => Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []),
  createQuoteTemplate: (data) => apiClient.post('/v1/settings?action=create_quote_template', data),
  updateQuoteTemplate: (id, data) => apiClient.put('/v1/settings', { action: 'update_quote_template', template_id: id, ...data }),
  deleteQuoteTemplate: (id) => apiClient.post('/v1/settings?action=delete_quote_template', { template_id: id }),

  // T&C Templates
  getTCTemplates: () => apiClient.get('/v1/settings?action=tc_templates').then(r => Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []),
  createTCTemplate: (data) => apiClient.post('/v1/settings?action=create_tc_template', data),
  updateTCTemplate: (id, data) => apiClient.put('/v1/settings', { action: 'update_tc_template', template_id: id, ...data }),
  deleteTCTemplate: (id) => apiClient.post('/v1/settings?action=delete_tc_template', { template_id: id }),

  // Project Lists (generic CRUD)
  // IDI-208 perf fix (2026-08-31): 'pricebook' specifically is routed through
  // the shared settingsListCache (dispatch 413's infra, already used by
  // useSettingsList elsewhere) -- live-measured 8.1s / 1.86MB per call, and
  // multiple page components (QuoteBuilderPage, QuoteItemsPage) call
  // getList('pricebook') independently, each paying that cost on every
  // mount with zero caching. Other list types are NOT routed through this
  // cache here -- they're small/fast, and touching them is outside this
  // fix's scope. Cache invalidates correctly on any pricebook write via
  // addListItem/updateListItem/deleteListItem/reorderList below (all
  // already call invalidateSettingsListCache(listType) on success).
  // IDI-338: the dealer's OWN coverage (and cost) for one catalog sku, written
  // to that dealer's pricebook override row only (never the shared base).
  // IDI-507 (4): a saved override changes what listPricebook returns, so the
  // in-memory pricebook must go, or the catalog page and the quote builder keep
  // showing (and pricing) the old cost/coverage until a full reload. Dropped
  // only after a successful write; silently (see settingsListCache.drop).
  savePricebookOverride: async (body) => {
    const res = await apiClient.post('/v1/settings?action=pricebook_override', body);
    dropSettingsListCache('pricebook');
    return res;
  },
  // IDI-509: Bulk Change / Undo writes the dealer's own price rows in one POST:
  // { changes: [{ sku, cost_cents }], undo? } -> { saved, failed: [{ sku }] }.
  bulkChangePrices: async (body) => {
    try {
      return await apiClient.post('/v1/settings?action=bulk_price_change', body);
    } finally {
      dropSettingsListCache('pricebook');
    }
  },
  // IDI-43: preview a dealer's CSV price sheet (action=price_upload, IDI-669/698);
  // with save: true it writes the changed rows (IDI-670). Any Save, even a failed
  // one, may have written rows, so the pricebook copy is dropped silently (D3).
  uploadPrices: async (body) => {
    try {
      return await apiClient.post('/v1/settings?action=price_upload', body);
    } finally {
      if (body && body.save === true) dropSettingsListCache('pricebook');
    }
  },
  // IDI-507 (7): is a catalog item used by any quote, job or change order?
  getCatalogItemUsage: (itemId) =>
    apiClient.get(`/v1/settings?action=catalog_item_usage&item_id=${encodeURIComponent(itemId)}`),
  getList: (listType) => {
    if (listType === 'pricebook') {
      const cached = getCachedSettingsList('pricebook');
      if (cached) return Promise.resolve(cached.items);
    }
    return apiClient.get(`/v1/settings?action=list&list_type=${listType}`).then((r) => {
      // IDI-293: was `Array.isArray(r) ? r : r.data ? ... : []`, which turned
      // ANY unrecognised response into "empty list", and an empty list is what
      // the seeding writers keyed on. unwrapList still returns a renderable []
      // but logs the contract break loudly (IDI-100).
      const items = unwrapList(r, Array.isArray(r?.data) || !Array.isArray(r?.items) ? 'data' : 'items', `settingsAPI.getList(${listType})`).list;
      if (listType === 'pricebook') {
        setCachedSettingsList('pricebook', { items });
      }
      return items;
    });
  },
  // PR-72 Commit 3 fixup (dispatch 413): each successful mutation
  // invalidates the useSettingsList cache for that listType. On failure
  // the cache is untouched — a rejected write must not present stale
  // data as a "clean" state to future consumers, and it must not
  // pretend the write happened. The caller sees the original error.
  addListItem: async (listType, data) => {
    const res = await apiClient.post('/v1/settings?action=add_list_item', { list_type: listType, ...data });
    invalidateSettingsListCache(listType);
    return res;
  },
  updateListItem: async (listType, id, data) => {
    const res = await apiClient.put('/v1/settings', { action: 'update_list_item', list_type: listType, item_id: id, ...data });
    invalidateSettingsListCache(listType);
    return res;
  },
  deleteListItem: async (listType, id) => {
    const res = await apiClient.post('/v1/settings?action=delete_list_item', { list_type: listType, item_id: id });
    invalidateSettingsListCache(listType);
    return res;
  },
  reorderList: async (listType, items) => {
    const res = await apiClient.put('/v1/settings', { action: 'reorder_list', list_type: listType, items });
    invalidateSettingsListCache(listType);
    return res;
  },

  // Notifications
  getNotifications: () => apiClient.get('/v1/settings?action=notifications'),
  updateNotifications: (data) => apiClient.put('/v1/settings', { action: 'notifications', ...data }),

  // Subscription & Billing
  getBilling: () => apiClient.get('/v1/settings?action=billing'),
};

// Super-admin (IDI tenant). Dealers are stored as a list on the IDI settings
// record. The super-admin JWT carries tenant_id = 'IDI', so these calls resolve
// to the IDI-scoped settings record server-side.
export const superAdminAPI = {
  // IDI-622 (D1): the open dealer's own Users & Roles logins (list_users with
  // scope=tenant; the dealer page sends the dealer as X-Tenant-Id).
  getDealerUsers: () => settingsAPI.getTenantUsers(),
  // IDI-252 (S2): create a login on the open dealer (X-Tenant-Id from the URL,
  // set by the request interceptor). apiClient.post already strips the
  // {success, data} envelope; resolves to {user, temporaryPassword, emailStatus}.
  addDealerUser: (data) => apiClient.post('/v1/settings?action=add_user', data)
    .then((r) => (r && r.data && r.data.user ? r.data : r)),
  // IDI-623 (D6): issue the open dealer's login a one-time password (never
  // emailed). Resolves to {user_id, email, temporaryPassword, emailStatus}.
  // IDI-226 (S2): switch the open dealer's login Paid↔Viewer (update_seat_type,
  // X-Tenant-Id from the URL). A 422 means the dealer has no paid seat free.
  updateDealerUserSeat: (userId, seatType) => settingsAPI.updateUserSeatType(userId, seatType),
  issueTempPassword: (userId) => apiClient.post('/v1/settings?action=issue_temp_password', { user_id: userId })
    .then((r) => (r && r.data && r.data.temporaryPassword ? r.data : r)),
  getDealers: () =>
    apiClient
      .get('/v1/settings?action=list&list_type=dealers')
      .then((r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.items) ? r.items : [])),
  getDealer: (id) =>
    apiClient
      .get('/v1/settings?action=list&list_type=dealers')
      .then((r) => {
        const arr = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        return arr.find((d) => (d.id || d.dealerId) === id) || null;
      }),
  // Applies masterDefaults ONCE at creation (D4: no retro push). Snapshots the
  // current master template into the dealer record so it is recorded at creation.
  addDealer: async (data) => {
    let appliedDefaults = {};
    try {
      appliedDefaults = await superAdminAPI.getMasterDefaults();
    } catch (e) {
      appliedDefaults = {};
    }
    return apiClient.post('/v1/settings?action=add_list_item', {
      list_type: 'dealers',
      appliedDefaults,
      ...data,
    });
  },
  updateDealer: (id, data) =>
    apiClient.put('/v1/settings', { action: 'update_list_item', list_type: 'dealers', item_id: id, ...data }),
  deleteDealer: (id) => apiClient.post('/v1/settings?action=delete_list_item', { list_type: 'dealers', item_id: id }),
  getMasterDefaults: () =>
    apiClient
      .get('/v1/settings?action=master_defaults')
      .then((r) => (r && typeof r === 'object' ? (r.data || r) : {})),
  updateMasterDefaults: (data) =>
    apiClient.put('/v1/settings', { action: 'master_defaults', ...data }),
  // IDI-621: a dealer's super-admin change history, newest first.
  // IDI-661: one page of 50 → { list, hasMore, nextCursor }; pass nextCursor for the next.
  getAuditLog: (dealerId, cursor) =>
    apiClient
      .get(`/v1/settings?action=audit_log&dealer_id=${encodeURIComponent(dealerId)}` +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''))
      .then((r) => unwrapList(Array.isArray(r) || Array.isArray(r?.entries) ? r : r?.data ?? r, 'entries', 'getAuditLog')),
  getReports: () =>
    apiClient
      .get('/v1/settings?action=list&list_type=dealers')
      .then((r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [])),
};

export const onboardingAPI = {
  getState: () => apiClient.getOnboardingState(),
  updateStep: (step, data, skipped) => apiClient.updateOnboardingStep(step, data, skipped),
  claim: (code) => apiClient.claimAccount(code),
  inviteTech: (email, name) => apiClient.inviteFieldTech(email, name),
  importCustomers: (customers, dryRun) => apiClient.importCustomers(customers, dryRun),
  complete: () => apiClient.completeOnboarding(),
};

export const taxAPI = {
  getAllStates: () => apiClient.getTaxStates(),
  getState: (state) => apiClient.getTaxState(state),
  calculate: (data) => apiClient.calculateTax(data),
};

export const telemetryAPI = {
  emit: (eventName, properties, idempotencyKey) => apiClient.emitEvent(eventName, properties, idempotencyKey),
};

export const reportAPI = {
  getSummary: (params) => apiClient.getReportSummary(params),
  getRevenue: (params) => apiClient.getReportRevenue(params),
  getQuotes: (params) => apiClient.getReportQuotes(params),
  getJobs: (params) => apiClient.getReportJobs(params),
  getMargins: (params) => apiClient.getReportMargins(params),
};

// Legacy APIs — kept for backward compatibility with existing components
export const userAPI = {
  getProfile: () => Promise.resolve({}),
  updateProfile: (data) => Promise.resolve(data),
  getUsers: () => apiClient.get('/v1/settings?action=list_users'),
  createUser: (data) => apiClient.post('/v1/settings?action=add_user', data),
  inviteUser: (data) => apiClient.post('/v1/settings?action=add_user', data),
  deactivateUser: (userId) => apiClient.post('/v1/settings?action=deactivate', { user_id: userId }),
};

export const authAPI = {
  login: async (email, password) => apiClient.post('/auth/login', { email, password }),
  validateToken: async (token) => apiClient.get('/auth/validate', { token }),
  refreshToken: async () => apiClient.post('/auth/refresh'),
};

// workOrderAPI removed (L-3 / Iteration 12 remediation): work orders are NOT a
// standalone resource. They live embedded in job.work_orders[] and are read/
// written exclusively via the /v1/jobs endpoints (jobAPI, api.getJob/updateJob).
// The prior workOrderAPI referenced apiClient.getWorkOrder/createWorkOrder/etc.
// methods that were already deleted (they hit the never-created bidit-dev-
// workorders table and always returned HTTP 500), so this export was dead AND
// broken (it would throw "apiClient.getWorkOrder is not a function" if called).
// It had no importers. Do NOT re-add without first provisioning the table+GSIs.

export const calendarAPI = {
  getEvents: (params) => apiClient.get('/v1/calendar/events', params),
  createAppointment: (data) => apiClient.post('/v1/calendar/appointments', data),
  createTask: (data) => apiClient.post('/v1/calendar/tasks', data),
  updateEvent: (id, data) => apiClient.put(`/v1/calendar/events/${id}`, data),
  deleteEvent: (id) => apiClient.delete(`/v1/calendar/events/${id}`),
};

export const changeOrderAPI = {
  getChangeOrders: (jobId) => apiClient.get(`/v1/jobs/${jobId}/change-orders`),
  getChangeOrderCustomerView: (id) => apiClient.getChangeOrderCustomerView(id),
  generateChangeOrderPDF: (id, body) => apiClient.generateChangeOrderPDF(id, body),
  createChangeOrder: (data) => apiClient.post('/v1/change-orders', data),
  updateChangeOrder: (id, data) => apiClient.put(`/v1/change-orders/${id}`, data),
  sendChangeOrder: (id, sendData) => apiClient.post(`/v1/change-orders/${id}/send`, sendData),
};

export const companyAPI = {
  getCompanies: () => Promise.resolve([]),
  getCompany: (id) => Promise.resolve({}),
  createCompany: (data) => Promise.resolve(data),
  updateCompany: (id, data) => Promise.resolve(data),
  deleteCompany: (id) => Promise.resolve({}),
};

export const leadAPI = {
  getLeads: () => apiClient.getCustomers().then((res) => {
    // IDI-120: getCustomers() now defaults to limit=250 and returns the
    // enveloped {customers, has_more, next_cursor} shape, not a bare array --
    // unwrap defensively so this doesn't silently return [] the moment a
    // caller relies on it.
    const { list } = unwrapList(res, 'customers', 'leadAPI.getLeads');
    return list.filter((c) => c.lead_status && c.lead_status !== 'converted');
  }),
  getLead: (id) => apiClient.getCustomer(id),
  createLead: (data) => apiClient.createCustomer({ ...data, lead_status: 'new' }),
  updateLead: (id, data) => apiClient.updateCustomer(id, data),
  deleteLead: (id) => apiClient.deleteCustomer(id),
  convertToCustomer: (id) => apiClient.updateCustomer(id, { lead_status: 'converted' }),
};

export default apiClient;
export { apiClient as api };
